import { useEffect } from 'react';

const APP_HEIGHT_VAR = '--app-height';

/**
 * Sets `--app-height` to the WebView layout height.
 * Prefer `window.innerHeight` over `visualViewport.height` alone — the latter is often
 * shorter in native iOS WKWebViews and leaves white letterbox gaps above/below the app.
 */
export function setAppViewportHeight() {
  const inner = window.innerHeight || document.documentElement.clientHeight;
  const vv = window.visualViewport;
  // Include offsetTop so a scrolled visual viewport still covers the layout viewport.
  const visual = vv ? vv.height + vv.offsetTop : 0;
  const height = Math.round(Math.max(inner, visual));
  document.documentElement.style.setProperty(APP_HEIGHT_VAR, `${height}px`);
}

/**
 * Keeps `--app-height` in sync with the visible viewport.
 * Prefer this over `100dvh` / `-webkit-fill-available` in native iOS WebViews —
 * those either leave letterbox gaps or break nested scroll containers.
 */
export function useAppViewportHeight() {
  useEffect(() => {
    setAppViewportHeight();

    const onResize = () => setAppViewportHeight();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('scroll', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('scroll', onResize);
    };
  }, []);
}
